// React imports
import { useMemo } from "react";

// Shared lib imports
import CenteredSpinner from "shared/ui/spinners/centered-spinner";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { ContactDetailI } from "shared/lib/interfaces/account";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { AccountDetailsSectionContainer } from "shared/ui/account-details/component-wrapper";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";

// Internal app imports
import { ACCOUNT_DETAILS_CREATE_NEW_LEAD_MODAL_ID } from "@/modules/pipeline/account-details/modals";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { AccountDetailsLeadItem } from "@/components/modules/pipeline/account-details/sections/leads-section/lead-item";
import { ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME } from "@/modules/pipeline/account-details/constants";

interface LeadsSectionPropsI extends PropsWithClassNameI {
  title?: string;
  filterFunc?: (contact: ContactDetailI) => boolean;
  isBookMeetingFlow?: boolean;
}

const openAddLeadModal = () =>
  modalHelpers.open(ACCOUNT_DETAILS_CREATE_NEW_LEAD_MODAL_ID);

const Header = ({ title = "Leads" }: { title?: string }) => {
  return (
    <div className="flex w-full items-center justify-between">
      <h6>{title}</h6>

      <button
        className={ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME}
        onClick={openAddLeadModal}
      >
        Add
      </button>
    </div>
  );
};

export const LeadsSection = ({
  className,
  filterFunc,
  title,
  isBookMeetingFlow,
}: LeadsSectionPropsI) => {
  const { contacts: allContacts, accountDetailsApi } =
    useAccountDetailsContext();

  const contacts = useMemo(
    () => (filterFunc ? allContacts?.filter(filterFunc) : allContacts),
    [allContacts, filterFunc]
  );

  return (
    <AccountDetailsSectionContainer
      title={<Header title={title} />}
      className={className}
      contentClassName="py-0"
    >
      <DataStatesWrapper
        api={accountDetailsApi}
        loading={<CenteredSpinner className="h-[160px]" />}
      >
        {contacts && (
          <div className="flex flex-col divide-y">
            {contacts.map((contact) => (
              <AccountDetailsLeadItem
                key={contact.id}
                contact={contact}
                className="lg:min-w-none py-3"
                isBookMeetingFlow={isBookMeetingFlow}
              />
            ))}
          </div>
        )}
      </DataStatesWrapper>
    </AccountDetailsSectionContainer>
  );
};
